import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Calculator, Plus, Search, Filter, Eye, Edit, Trash2,
  Send, CheckCircle, Clock, XCircle, FileText, Mail, MessageCircle, Printer
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import Badge from '../components/ui/Badge'
import Pagination from '../components/ui/Pagination'
import Modal, { ConfirmModal } from '../components/Modal'
import { useToast } from '../components/Toast'
import orcamentosService, { Orcamento } from '../services/orcamentos'
import notificationsService from '../services/notifications'
import settingsService from '../services/settings'
import { previewCupom, DadosImpressao, imprimirCupom } from '../utils/impressaoTermica'

const OrcamentosPage: React.FC = () => {
  const navigate = useNavigate()
  const { success, error } = useToast()

  // Estados
  const [filters, setFilters] = useState({
    page: 1,
    limit: 25,
    search: '',
    status: '',
    clienteId: undefined as number | undefined
  })
  const [showFilters, setShowFilters] = useState(false)
  const [orcamentoToDelete, setOrcamentoToDelete] = useState<Orcamento | null>(null)
  const [showResendModal, setShowResendModal] = useState(false)
  const [orcamentoToResend, setOrcamentoToResend] = useState<Orcamento | null>(null)
  const [resendOptions, setResendOptions] = useState({ email: false, whatsapp: false })
  const [isResending, setIsResending] = useState(false)

  // Buscar orçamentos do backend
  const { data: orcamentosResponse, isLoading, refetch } = useQuery({
    queryKey: ['orcamentos', filters],
    queryFn: () => orcamentosService.list(filters),
    staleTime: 1000 * 60 * 5, // Cache por 5 minutos
  })

  const orcamentos = orcamentosResponse?.orcamentos || []
  const pagination = orcamentosResponse?.pagination || {
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  }

  const getStatusConfig = (status: Orcamento['status']) => {
    const configs = {
      RASCUNHO: { color: 'gray' as const, icon: FileText, label: 'Rascunho' },
      ENVIADO: { color: 'blue' as const, icon: Send, label: 'Enviado' },
      APROVADO: { color: 'green' as const, icon: CheckCircle, label: 'Aprovado' },
      REJEITADO: { color: 'red' as const, icon: XCircle, label: 'Rejeitado' },
      EXPIRADO: { color: 'orange' as const, icon: Clock, label: 'Expirado' }
    }
    return configs[status] || configs.RASCUNHO
  }

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value, page: 1 }))
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-PT')
  }

  const handleDelete = async () => {
    if (!orcamentoToDelete) return

    try {
      await orcamentosService.delete(orcamentoToDelete.id)
      success('Orçamento excluído com sucesso!')
      setOrcamentoToDelete(null)
      refetch()
    } catch (err: any) {
      error(`Erro ao excluir orçamento: ${err.message}`)
    }
  }

  // Função para gerar token público (similar ao backend)
  const generatePublicToken = (orcamentoId: number, criadoEm: string): string => {
    return btoa(`${orcamentoId}-${criadoEm}`)
      .replace(/[^a-zA-Z0-9]/g, '')
      .substring(0, 16)
  }

  // Abrir modal de reenvio
  const handleResendClick = (orcamento: Orcamento) => {
    setOrcamentoToResend(orcamento)
    setResendOptions({
      email: !!orcamento.cliente.email,
      whatsapp: !!orcamento.cliente.telefonePrincipal
    })
    setShowResendModal(true)
  }

  // Reenviar orçamento
  const handleResend = async () => {
    if (!orcamentoToResend) return

    setIsResending(true)
    try {
      let successCount = 0
      const attempts = []

      // Enviar por email se selecionado
      if (resendOptions.email && orcamentoToResend.cliente.email) {
        attempts.push('email')
        // TODO: Implementar envio por email quando serviço estiver disponível
        // await emailService.sendOrcamento(orcamentoToResend.id)
        successCount++
      }

      // Enviar por WhatsApp se selecionado
      if (resendOptions.whatsapp && orcamentoToResend.cliente.telefonePrincipal) {
        attempts.push('WhatsApp')
        await notificationsService.sendOrcamentoNotification(orcamentoToResend.id, 'CRIACAO')
        successCount++
      }

      if (successCount > 0) {
        success(`Orçamento reenviado com sucesso por ${attempts.join(' e ')}!`)
      } else {
        error('Nenhum método de envio foi selecionado ou disponível')
      }

      setShowResendModal(false)
      setOrcamentoToResend(null)
      setResendOptions({ email: false, whatsapp: false })
    } catch (err: any) {
      error(`Erro ao reenviar orçamento: ${err.response?.data?.message || err.message}`)
    } finally {
      setIsResending(false)
    }
  }

  // Imprimir cupom do orçamento
  const handlePrint = async (orcamento: Orcamento) => {
    try {
      // Buscar configurações da empresa e sistema
      const empresaSettings = await settingsService.getEmpresaSettings()
      const systemSettings = await settingsService.getSystemSettings()

      // Verificar se impressão térmica está configurada
      if (!systemSettings.impressaoTermica?.enabled) {
        error('Impressão térmica não está configurada')
        return
      }

      // Dados para impressão
      const dadosImpressao: DadosImpressao = {
        id: orcamento.id,
        tipo: 'ORCAMENTO',
        numeroDocumento: orcamento.numero,
        dataHora: new Date().toLocaleString('pt-PT'),
        dadosEmpresa: {
          nome: empresaSettings.nome || 'NexaMakers',
          nif: empresaSettings.nif || '',
          endereco: empresaSettings.morada || '',
          telefone: empresaSettings.telefone || '',
          email: empresaSettings.email || '',
          website: empresaSettings.website || ''
        },
        dadosCliente: {
          nome: orcamento.cliente.nome,
          telefone: orcamento.cliente.telefonePrincipal || '',
          email: orcamento.cliente.email || ''
        },
        dadosEquipamento: {
          tipo: orcamento.ordemServico.equipamento.modelo.tipoEquipamento.nome,
          marca: orcamento.ordemServico.equipamento.modelo.marca.nome,
          modelo: orcamento.ordemServico.equipamento.modelo.nome,
          numeroSerie: orcamento.ordemServico.equipamento.numeroSerie || '',
          observacoes: orcamento.ordemServico.problemaRelatado
        },
        itens: orcamento.itens.map(item => ({
          descricao: item.descricao,
          quantidade: item.quantidade,
          valorUnitario: item.valorUnitario,
          valorTotal: item.valorTotal
        })),
        valorTotal: orcamento.valorTotal,
        observacoes: `${orcamento.observacoesCliente || ''}\n\nPrazo: ${orcamento.prazoReparacao}\nGarantia: ${orcamento.garantia}\nVálido até: ${new Date(orcamento.validadeAte).toLocaleDateString('pt-PT')}`
      }

      await imprimirCupom(dadosImpressao, systemSettings.impressaoTermica)
      success('Cupom do orçamento impresso com sucesso!')
    } catch (err: any) {
      error(`Erro ao imprimir: ${err.message}`)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Calculator className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Orçamentos</h1>
              <p className="text-gray-600">Gerir orçamentos de serviços</p>
            </div>
          </div>

          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => navigate('/orcamentos/novo')}
          >
            Novo Orçamento
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 space-y-6">
        {/* Filtros */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 max-w-md">
              <Input
                placeholder="Pesquisar orçamentos..."
                leftIcon={<Search className="h-4 w-4" />}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>

            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Filter className="h-4 w-4" />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filtros
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
              <Select
                label="Status"
                options={[
                  { value: '', label: 'Todos os status' },
                  { value: 'RASCUNHO', label: 'Rascunho' },
                  { value: 'ENVIADO', label: 'Enviado' },
                  { value: 'APROVADO', label: 'Aprovado' },
                  { value: 'REJEITADO', label: 'Rejeitado' },
                  { value: 'EXPIRADO', label: 'Expirado' }
                ]}
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Tabela */}
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Orçamento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Equipamento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-gray-500">Carregando orçamentos...</p>
                    </td>
                  </tr>
                ) : orcamentos.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      Nenhum orçamento encontrado
                    </td>
                  </tr>
                ) : (
                  orcamentos.map((orcamento) => {
                    const statusConfig = getStatusConfig(orcamento.status)
                    const StatusIcon = statusConfig.icon
                    return (
                      <tr key={orcamento.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {orcamento.numero}
                            </div>
                            <div className="text-sm text-gray-500">
                              OS: {orcamento.ordemServico.numero}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {orcamento.cliente.nome}
                          </div>
                          <div className="text-sm text-gray-500">
                            {orcamento.cliente.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {orcamento.ordemServico.equipamento.modelo.marca.nome} {orcamento.ordemServico.equipamento.modelo.nome}
                          </div>
                          <div className="text-sm text-gray-500">{orcamento.ordemServico.problemaRelatado}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge
                            color={statusConfig.color}
                            icon={<StatusIcon className="h-3 w-3" />}
                          >
                            {statusConfig.label}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(orcamento.valorTotal)}
                          </div>
                          <div className="text-sm text-gray-500">
                            Válido até: {formatDate(orcamento.validadeAte)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-1">
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => navigate(`/orcamentos/${orcamento.id}`)}
                              title="Ver detalhes"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => navigate(`/orcamentos/${orcamento.id}/editar`)}
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {orcamento.status === 'ENVIADO' && (
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() => handleResendClick(orcamento)}
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                title="Reenviar orçamento"
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => handlePrint(orcamento)}
                              className="text-green-600 hover:text-green-800 hover:bg-green-50"
                              title="Imprimir cupom"
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => setOrcamentoToDelete(orcamento)}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50"
                              title="Excluir"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          <div className="bg-white px-4 py-3 border-t border-gray-200">
            <Pagination
              totalItems={pagination.total}
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              hasNext={pagination.hasNext}
              hasPrev={pagination.hasPrev}
              onPageChange={(page) => setFilters({ ...filters, page })}
              onPageSizeChange={(limit) => setFilters({ ...filters, limit, page: 1 })}
            />
          </div>
        </div>
      </div>

      {/* Modal de reenvio */}
      <Modal
        isOpen={showResendModal}
        onClose={() => {
          setShowResendModal(false)
          setOrcamentoToResend(null)
          setResendOptions({ email: false, whatsapp: false })
        }}
        title="Reenviar Orçamento"
        size="md"
      >
        {orcamentoToResend && (
          <div className="space-y-6">
            {/* Informações do orçamento */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-2">
                Orçamento: {orcamentoToResend.numero}
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div>📧 {orcamentoToResend.cliente.email || 'Email não cadastrado'}</div>
                <div>📱 {orcamentoToResend.cliente.telefonePrincipal || 'Telefone não cadastrado'}</div>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                💰 {formatCurrency(orcamentoToResend.valorTotal)}
              </div>
            </div>

            {/* Opções de reenvio */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Como deseja reenviar o orçamento?</h4>

              <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resendOptions.email}
                  onChange={(e) => setResendOptions({ ...resendOptions, email: e.target.checked })}
                  className="rounded border-gray-300"
                  disabled={!orcamentoToResend.cliente.email}
                />
                <Mail className="h-5 w-5 text-blue-500" />
                <div className="flex-1">
                  <div className="font-medium">Enviar por Email</div>
                  <div className="text-sm text-gray-500">
                    {orcamentoToResend.cliente.email || 'Email não cadastrado'}
                  </div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resendOptions.whatsapp}
                  onChange={(e) => setResendOptions({ ...resendOptions, whatsapp: e.target.checked })}
                  className="rounded border-gray-300"
                  disabled={!orcamentoToResend.cliente.telefonePrincipal}
                />
                <MessageCircle className="h-5 w-5 text-green-500" />
                <div className="flex-1">
                  <div className="font-medium">Enviar por WhatsApp</div>
                  <div className="text-sm text-gray-500">
                    {orcamentoToResend.cliente.telefonePrincipal || 'Telefone não cadastrado'}
                  </div>
                </div>
              </label>
            </div>

            {/* Botões */}
            <div className="flex justify-end space-x-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowResendModal(false)
                  setOrcamentoToResend(null)
                  setResendOptions({ email: false, whatsapp: false })
                }}
                disabled={isResending}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleResend}
                disabled={isResending || (!resendOptions.email && !resendOptions.whatsapp)}
                leftIcon={isResending ? undefined : <Send className="h-4 w-4" />}
              >
                {isResending ? 'Enviando...' : 'Reenviar'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de confirmação de exclusão */}
      <ConfirmModal
        isOpen={!!orcamentoToDelete}
        onClose={() => setOrcamentoToDelete(null)}
        onConfirm={handleDelete}
        title="Excluir Orçamento"
        message={`Tem a certeza que deseja excluir o orçamento ${orcamentoToDelete?.numero}? Esta ação não pode ser desfeita.`}
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        variant="danger"
      />
    </div>
  )
}

export default OrcamentosPage